import { useState } from "react";
import { ChevronRight, Flame, Send, FileText, BookOpen } from "lucide-react";
import { AdminSheet } from "@/components/admin/AdminSheet";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DONE = [true, true, true, true, true, false, false];

function Line({ pts }: { pts: number[] }) {
  const max = Math.max(...pts), min = Math.min(...pts);
  const d = pts.map((p, i) => `${(i / (pts.length - 1)) * 120},${44 - ((p - min) / (max - min || 1)) * 38}`).join(" ");
  return (
    <svg viewBox="0 0 120 48" preserveAspectRatio="none" className="h-12 w-full">
      <polyline points={d} fill="none" stroke="hsl(var(--gold))" strokeWidth="1.5" />
    </svg>
  );
}

const Box = ({ children }: { children: React.ReactNode }) => (
  <section className="rounded-xl border border-amber-500/30 bg-card/80 p-3">{children}</section>
);

function ExampleReview() {
  return (
    <div className="space-y-3">
      <p className="rounded-md border border-dashed border-gold/60 bg-gold/10 px-3 py-1.5 text-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
        Example · sample numbers · only you see this
      </p>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-gold bg-gold/15 font-display text-2xl">E</div>
        <div>
          <p className="font-display text-xl">Example Member</p>
          <p className="text-xs text-muted-foreground">Stage 1 of 8 · Encumbered</p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2 w-2 rounded-full bg-green-600" />Active · Last seen 2h ago</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <span className="flex h-9 items-center justify-center gap-1.5 rounded-md bg-gold text-sm text-primary-foreground"><Send className="h-4 w-4" />Nudge</span>
        <span className="flex h-9 items-center justify-center gap-1.5 rounded-md bg-gold text-sm text-primary-foreground"><FileText className="h-4 w-4" />Add note</span>
      </div>
      <Box>
        <h3 className="mb-2 font-display text-base">This week</h3>
        <div className="grid grid-cols-7 text-center text-[10px] text-muted-foreground">
          {DAYS.map((d, i) => (
            <div key={d}>{d}<div className={`mx-auto mt-1 h-5 w-5 rounded-full border border-gold ${DONE[i] ? "bg-gold" : ""}`} /></div>
          ))}
        </div>
      </Box>
      <Box>
        <h3 className="mb-2 font-display text-base">Trends <span className="text-xs text-muted-foreground">(last 8 weeks)</span></h3>
        <div className="grid grid-cols-3 gap-2">
          {[["Savings", [1, 1.4, 1.3, 2, 2.4, 2.3, 3, 3.6]], ["Debt", [9, 8.6, 8.8, 8, 7.5, 7.2, 6.6, 6]], ["Body fat", [35, 34.6, 34.8, 34, 33.4, 33.5, 32.8, 32.4]]].map(([n, p]) => (
            <div key={n as string} className="rounded-lg border border-amber-500/20 p-2 text-[11px]">{n as string}<Line pts={p as number[]} /></div>
          ))}
        </div>
      </Box>
      <Box>
        <h3 className="mb-2 font-display text-base">Homework</h3>
        <div className="flex items-center gap-2">
          <div className="flex h-11 w-12 items-center justify-center rounded bg-gold/15"><BookOpen className="h-5 w-5 text-gold" /></div>
          <div className="flex-1 text-xs"><p className="font-medium">Read Chapter 2</p><p className="text-muted-foreground">Due Apr 27</p></div>
          <span className="rounded bg-gold px-3 py-1 text-xs text-primary-foreground">Pass</span>
          <span className="rounded border border-gold px-3 py-1 text-xs">Redo</span>
        </div>
      </Box>
      <Box>
        <h3 className="mb-2 font-display text-base">Coach notes <span className="text-xs text-muted-foreground">(private)</span></h3>
        <ul className="space-y-2 border-l border-gold/50 pl-3 text-xs">
          <li><p className="text-muted-foreground">Apr 24</p>Good progress this week. More consistent mornings.</li>
          <li><p className="text-muted-foreground">Apr 20</p>Struggling a bit with spending. Discussed triggers.</li>
        </ul>
      </Box>
      <div className="flex items-center gap-2 rounded-xl border border-gold/50 bg-gold/10 px-3 py-2 text-xs">
        <Send className="h-4 w-4 text-gold" /><div className="flex-1"><p>Nudge sent</p><p className="text-muted-foreground">Seen 2h ago</p></div><ChevronRight className="h-4 w-4" />
      </div>
      <p className="pt-2 text-center font-serif text-[11px] uppercase italic tracking-[0.25em] text-muted-foreground">Small steps. Bigger lives.</p>
    </div>
  );
}

export default function ExampleMemberCard() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="mb-3 flex w-full items-center gap-3 rounded-xl border border-dashed border-gold/60 bg-card/80 p-3 text-left">
        <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-gold bg-gold/15 font-display text-xl">E</div>
        <div className="flex-1">
          <p className="font-display text-lg">Example Member</p>
          <p className="text-xs text-muted-foreground">Stage 1 · Sample data · only you see this</p>
          <p className="flex items-center gap-1 text-xs text-muted-foreground"><Flame className="h-3.5 w-3.5 text-gold" />5 days</p>
        </div>
        <div className="flex flex-col items-center gap-3"><span className="h-2.5 w-2.5 rounded-full bg-green-600" /><ChevronRight className="h-4 w-4" /></div>
      </button>
      <AdminSheet open={open} onOpenChange={setOpen} title="Member Review" description="Example" size="md">
        <ExampleReview />
      </AdminSheet>
    </>
  );
}
